import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Check, Loader2, MapPin, Store } from "lucide-react";
import { supermarketIcon, selectedIcon, userIcon } from "./leaflet-config";
import MapRecenter from "./MapRecenter";
import { getSavingsLabel } from "./helpers";
import "leaflet/dist/leaflet.css";

export default function SupermarketSelector({
  supermarkets,
  loadingSupermarkets,
  supermarketError,
  selectedSupermarket,
  setSelectedSupermarket,
  supermarketSort,
  setSupermarketSort,
  showMap,
  setShowMap,
  userCoords,
  findNearbySupermarkets
}) {
  return (
    <Card className="border-none shadow-soft">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5 text-primary" />
            <CardTitle className="text-base">Supermercados Cercanos</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {supermarkets.length > 0 && (
              <>
                <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                  <button
                    onClick={() => setSupermarketSort("distance")}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                      supermarketSort === "distance" ? "bg-background shadow text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="sort-by-distance-btn"
                  >
                    <MapPin className="w-3 h-3" /> Más cercano
                  </button>
                  <button
                    onClick={() => setSupermarketSort("price")}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                      supermarketSort === "price" ? "bg-background shadow text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="sort-by-price-btn"
                  >
                    <span className="text-xs">€</span> Mejor precio
                  </button>
                </div>
                <Button
                  variant={showMap ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowMap((v) => !v)}
                  className="gap-1.5 h-8"
                  data-testid="toggle-map-btn"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {showMap ? "Ver lista" : "Ver mapa"}
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={findNearbySupermarkets}
              disabled={loadingSupermarkets}
              data-testid="find-supermarkets-btn"
              className="gap-2"
            >
              {loadingSupermarkets ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
              {loadingSupermarkets ? "Buscando..." : "Buscar cerca de mí"}
            </Button>
          </div>
        </div>
        {supermarketError && <p className="text-sm text-destructive mt-1">{supermarketError}</p>}
      </CardHeader>

      {supermarkets.length > 0 && (
        <CardContent className="pt-0 space-y-3">
          {/* Map view */}
          {showMap && userCoords && (
            <div className="h-64 rounded-xl overflow-hidden border relative" style={{ zIndex: 0 }}>
              <MapContainer
                center={[userCoords.lat, userCoords.lon]}
                zoom={14}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapRecenter lat={userCoords.lat} lon={userCoords.lon} />
                <Marker position={[userCoords.lat, userCoords.lon]} icon={userIcon}>
                  <Popup><span className="font-semibold text-sm">Tu ubicación</span></Popup>
                </Marker>
                {supermarkets.map((s) => (
                  <Marker
                    key={s.id}
                    position={[s.lat, s.lon]}
                    icon={selectedSupermarket?.id === s.id ? selectedIcon : supermarketIcon}
                    eventHandlers={{ click: () => setSelectedSupermarket(selectedSupermarket?.id === s.id ? null : s) }}
                  >
                    <Popup>
                      <div className="text-sm min-w-[140px]">
                        <p className="font-bold">{s.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.distance_km < 1 ? `${Math.round(s.distance_km * 1000)} m` : `${s.distance_km} km`}
                        </p>
                        <p className="text-xs">{s.price_label}</p>
                        {s.address && <p className="text-xs text-muted-foreground">{s.address}</p>}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}

          {/* Chips */}
          <div className="flex flex-wrap gap-2">
            {[...supermarkets]
              .sort((a, b) =>
                supermarketSort === "price"
                  ? a.price_tier - b.price_tier || a.distance_km - b.distance_km
                  : a.distance_km - b.distance_km
              )
              .map((s) => {
                const savings = supermarketSort === "price" ? getSavingsLabel(s.price_tier) : null;
                const isSelected = selectedSupermarket?.id === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSupermarket(isSelected ? null : s)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all ${
                      isSelected ? "bg-primary text-primary-foreground border-primary" : "bg-muted hover:bg-muted/80 border-border"
                    }`}
                    data-testid={`supermarket-chip-${s.id}`}
                  >
                    <Store className="w-3 h-3 flex-shrink-0" />
                    <span className="font-medium">{s.name}</span>
                    {supermarketSort === "distance" && (
                      <span className={`text-xs ${isSelected ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        · {s.distance_km < 1 ? `${Math.round(s.distance_km * 1000)} m` : `${s.distance_km} km`}
                      </span>
                    )}
                    {savings && (
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                        savings.saving
                          ? isSelected ? "bg-emerald-500/30 text-emerald-100" : "bg-emerald-100 text-emerald-700"
                          : isSelected ? "bg-amber-500/30 text-amber-100" : "bg-amber-100 text-amber-700"
                      }`}>
                        {savings.text}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>

          {selectedSupermarket && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-primary" />
                <strong className="text-foreground">{selectedSupermarket.name}</strong>
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {selectedSupermarket.distance_km < 1 ? `${Math.round(selectedSupermarket.distance_km * 1000)} m` : `${selectedSupermarket.distance_km} km`}
              </span>
              <span>{selectedSupermarket.price_label}</span>
              {selectedSupermarket.address && <span>· {selectedSupermarket.address}</span>}
            </div>
          )}
          {!selectedSupermarket && (
            <p className="text-xs text-muted-foreground">
              Selecciona un supermercado para organizar la lista por sus pasillos exactos.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
